import { LitElement, html } from 'lit';

class BeautySiblings extends LitElement {
  static get properties() {
    return {
      productId: { type: Number, attribute: 'product_id' },
      productHandle: { type: String, attribute: 'product_handle' },
      productTags: { type: String, attribute: 'product_tags' },
      siblingsData: { type: String, attribute: 'siblings_data' },
      variantsData: { type: String, attribute: 'variants_data' },
      currentVariantId: { type: Number, attribute: 'current_variant_id' },
      isLoading: { type: Boolean }
    };
  }

  constructor() {
    super();
    this.productId = 0;
    this.productHandle = '';
    this.productTags = '';
    this.siblingsData = '[]';
    this.variantsData = '[]';
    this.currentVariantId = 0;
    this.isLoading = false;
  }

  createRenderRoot() { 
    return this; 
  }

  get siblings() {
    try {
      return JSON.parse(this.siblingsData || '[]');
    } catch (error) {
      console.error('Error parsing siblings data:', error);
      return [];
    }
  }

  get variants() {
    try {
      return JSON.parse(this.variantsData || '[]');
    } catch (error) {
      console.error('Error parsing variants data:', error);
      return [];
    }
  }

  get filteredSiblings() {
    return this.siblings.filter(sibling => 
      !sibling.tags?.includes('availability:hidden') &&
      sibling.published_at
    );
  }

  get filteredVariants() {
    return this.variants.filter(variant => 
      variant.available && 
      variant.option1
    );
  }

  get currentProductSibling() {
    return this.siblings.find(sibling => sibling.handle === this.productHandle);
  }

  get currentVariant() {
    return this.variants.find(variant => variant.id === this.currentVariantId);
  }

  get isOneSize() {
    return this.productTags?.includes('size:one-size') || false;
  }

  getValueFromTags(tags) {
    const prefix = 'flavour:';
    for (const tag of tags) {
      if (tag.toLowerCase().startsWith(prefix)) {
        return tag.split(':').slice(1).join(':').trim();
      }
    }
    return null;
  }

  renderSibling(sibling) {
    const value = this.getValueFromTags(sibling.tags || []) || sibling.label;
    if (!value) return null;
    
    const isCurrentProduct = sibling.handle === this.productHandle;
    
    const onSelect = (e) => {
      e.preventDefault();
      this.dispatchEvent(new CustomEvent('beauty:sibling-select', { 
        detail: { sibling }, 
        bubbles: true, 
        composed: true 
      }));
    };

    return html`
      <div class="beauty_sibling_item">
        <button 
          class="beauty_sibling_swatch ${isCurrentProduct ? 'selected' : ''}"
          @click=${onSelect}
          title="${value}"
        >
          <span class="beauty_sibling_value">${value}</span>
        </button>
      </div>
    `;
  }

  renderVariant(variant) {
    const size = variant.option1;
    if (!size) return null;
    
    const isCurrentVariant = variant.id === this.currentVariantId;
    
    const onSelect = (e) => {
      e.preventDefault();
      this.dispatchEvent(new CustomEvent('beauty:variant-select', { 
        detail: { variant }, 
        bubbles: true, 
        composed: true 
      }));
    };

    return html`
      <div class="beauty_sibling_item">
        <button 
          class="beauty_sibling_swatch ${isCurrentVariant ? 'selected' : ''}"
          @click=${onSelect}
          title="${size}"
        >
          <span class="beauty_sibling_value">${size}</span>
        </button>
      </div>
    `;
  }

  render() {
    if (this.variantsData && this.variantsData !== '[]' && this.variantsData !== '') {
      const variants = this.filteredVariants;
      if (this.isOneSize || variants.length <= 1) {
        return html``;
      }
      return html`
        <div class="beauty_siblings">
          <div class="beauty_siblings_grid">
            ${variants.map(variant => this.renderVariant(variant))}
          </div>
        </div>
      `;
    }
    
    const siblings = this.filteredSiblings;
    if (siblings.length === 0) {
      return html``;
    }
    return html`
      <div class="beauty_siblings">
        <div class="beauty_siblings_grid">
          ${siblings.map(sibling => this.renderSibling(sibling))}
        </div>
      </div>
    `;
  }
}

customElements.define('beauty-siblings', BeautySiblings);